import { addDays, startOfDay, subDays, subMonths } from 'date-fns';

/**
 * Everything the analytics page keeps in the URL. Parsing is lenient so that a
 * hand-edited or stale link falls back to defaults instead of breaking.
 */
export const CATEGORIES = [
  'overview',
  'retention',
  'risk',
  'learning',
  'attendance',
  'chat',
  'feedback',
  'journey',
] as const;
export type Category = (typeof CATEGORIES)[number];

export const RANGE_PRESETS = ['7d', '30d', '90d', '6m', '12m', 'custom'] as const;
export type RangePreset = (typeof RANGE_PRESETS)[number];

export const GRAINS = ['day', 'week', 'month'] as const;
export type Grain = (typeof GRAINS)[number];
export type GrainChoice = Grain | 'auto';

export const STUDENT_TYPES = ['group', 'private', 'self-learning', 'community', 'b2b'] as const;
export type StudentType = (typeof STUDENT_TYPES)[number];

export const COMPARE_MODES = ['none', 'previous', 'lastYear'] as const;
export type CompareMode = (typeof COMPARE_MODES)[number];

export type AnalyticsSearch = {
  category: Category;
  range: RangePreset;
  from?: string; // yyyy-mm-dd, custom range only
  to?: string; // yyyy-mm-dd, custom range only (inclusive day)
  grain: GrainChoice;
  /** What every chart and tile is compared against. */
  compare: CompareMode;
  levelIds?: string[];
  groupIds?: string[];
  teacherIds?: string[];
  studentTypes?: StudentType[];
  includeDisabled?: boolean;
  session?: string; // chat transcript modal
  campaign?: string; // feedback campaign detail modal
};

export const DEFAULT_SEARCH: AnalyticsSearch = { category: 'overview', range: '30d', grain: 'auto', compare: 'previous' };

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : undefined;
}

function uuid(value: unknown): string | undefined {
  return typeof value === 'string' && UUID.test(value) ? value : undefined;
}

/** Accepts an array or a comma-separated string; keeps valid, distinct entries in order. */
function list<T extends string>(value: unknown, keep: (v: unknown) => T | undefined): T[] {
  const raw = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
  const out: T[] = [];
  for (const item of raw) {
    const v = keep(item);
    if (v !== undefined && !out.includes(v)) out.push(v);
  }
  return out;
}

function dateKey(value: unknown): string | undefined {
  return typeof value === 'string' && DATE_KEY.test(value) ? value : undefined;
}

export function parseSearch(raw: Record<string, unknown>): AnalyticsSearch {
  const out: AnalyticsSearch = {
    category: oneOf(raw.category, CATEGORIES) ?? DEFAULT_SEARCH.category,
    range: oneOf(raw.range, RANGE_PRESETS) ?? DEFAULT_SEARCH.range,
    grain: oneOf(raw.grain, ['auto', ...GRAINS] as const) ?? 'auto',
    compare: oneOf(raw.compare, COMPARE_MODES) ?? DEFAULT_SEARCH.compare,
  };
  const from = dateKey(raw.from);
  const to = dateKey(raw.to);
  if (out.range === 'custom' && from && to && from <= to) {
    out.from = from;
    out.to = to;
  } else if (out.range === 'custom') {
    out.range = DEFAULT_SEARCH.range;
  }
  const levelIds = list(raw.levelIds, uuid);
  const groupIds = list(raw.groupIds, uuid);
  const teacherIds = list(raw.teacherIds, uuid);
  const studentTypes = list(raw.studentTypes, (v) => oneOf(v, STUDENT_TYPES));
  const session = uuid(raw.session);
  const campaign = uuid(raw.campaign);
  if (levelIds.length) out.levelIds = levelIds;
  if (groupIds.length) out.groupIds = groupIds;
  if (teacherIds.length) out.teacherIds = teacherIds;
  if (studentTypes.length) out.studentTypes = studentTypes;
  if (raw.includeDisabled === true || raw.includeDisabled === 'true') out.includeDisabled = true;
  if (session) out.session = session;
  if (campaign) out.campaign = campaign;
  return out;
}

export function parseDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * The absolute window a preset means, in the viewer's local time.
 * `to` is exclusive (start of the day after the last day shown).
 */
export function resolveRange(search: AnalyticsSearch, now: Date = new Date()): { from: Date; to: Date } {
  const today = startOfDay(now);
  const tomorrow = addDays(today, 1);
  switch (search.range) {
    case '7d':
      return { from: subDays(tomorrow, 7), to: tomorrow };
    case '30d':
      return { from: subDays(tomorrow, 30), to: tomorrow };
    case '90d':
      return { from: subDays(tomorrow, 90), to: tomorrow };
    case '6m':
      return { from: subMonths(tomorrow, 6), to: tomorrow };
    case '12m':
      return { from: subMonths(tomorrow, 12), to: tomorrow };
    case 'custom': {
      const from = search.from ? startOfDay(parseDateKey(search.from)) : subDays(tomorrow, 30);
      const to = search.to ? addDays(startOfDay(parseDateKey(search.to)), 1) : tomorrow;
      return to > from ? { from, to } : { from: subDays(tomorrow, 30), to: tomorrow };
    }
    default:
      return { from: subDays(tomorrow, 30), to: tomorrow };
  }
}

/** Day for short windows, week for a season, month for a year. */
export function autoGrain(from: Date, to: Date): Grain {
  const days = (to.getTime() - from.getTime()) / 86_400_000;
  if (days <= 45) return 'day';
  if (days <= 200) return 'week';
  return 'month';
}

export function resolveGrain(search: AnalyticsSearch, range: { from: Date; to: Date }): Grain {
  return search.grain === 'auto' ? autoGrain(range.from, range.to) : search.grain;
}

export type FilterInput = {
  from: string;
  to: string;
  grain: Grain;
  levelIds?: string[];
  groupIds?: string[];
  teacherIds?: string[];
  studentTypes?: StudentType[];
  includeDisabled?: boolean;
};

/** The exact object every report procedure takes. */
export function toFilterInput(search: AnalyticsSearch, now: Date = new Date()): FilterInput {
  const range = resolveRange(search, now);
  const input: FilterInput = {
    from: range.from.toISOString(),
    to: range.to.toISOString(),
    grain: resolveGrain(search, range),
  };
  if (search.levelIds?.length) input.levelIds = search.levelIds;
  if (search.groupIds?.length) input.groupIds = search.groupIds;
  if (search.teacherIds?.length) input.teacherIds = search.teacherIds;
  if (search.studentTypes?.length) input.studentTypes = search.studentTypes;
  if (search.includeDisabled) input.includeDisabled = true;
  return input;
}

/** Days covered by the window, for "vs previous N days" labels. */
export function rangeLengthDays(range: { from: Date; to: Date }): number {
  return Math.round((range.to.getTime() - range.from.getTime()) / 86_400_000);
}
